import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/shared/ui';

import { listBatchConfirmable, type NoteAssumption } from '../lib';

import { AiBasisQuote } from './AiBasisQuote';
import styles from './NoteAssumptions.module.scss';

// The list stays short until the clinician asks for every line.
const SHOWN_LINES = 5;

// What a note reports to the page that holds "Save to record": its
// assumptions and the editor's own actions on them, so a confirmation made
// there is in the note's undo history.
export type NoteAssumptionsReport = {
  assumptions: NoteAssumption[];
  // Absent while the note is read-only.
  confirmAll?: () => void;
  confirm?: (assumption: NoteAssumption) => void;
  showLine: (lineId: string) => void;
};

type Props = {
  assumptions: NoteAssumption[];
  className?: string;
  // Shows the line in the note; without it the lines are text.
  onSelect?: (lineId: string) => void;
  // Confirms every assumption but the diagnosis codes.
  onConfirmAll?: () => void;
  onConfirm?: (assumption: NoteAssumption) => void;
};

// Scribe refuses to approve a block note while an AI value nobody said is
// unconfirmed, so "Save to record" and copying wait and the values are
// listed by line with the words each was read from, to confirm all at once
// or each in its line. A diagnosis code is confirmed on its own.
export const NoteAssumptions: FC<Props> = ({
  assumptions,
  className,
  onSelect,
  onConfirmAll,
  onConfirm,
}) => {
  const { formatMessage } = useIntl();
  const [allShown, setAllShown] = useState(false);
  const lines = assumptions.filter(
    (assumption, index) =>
      assumptions.findIndex((item) => item.lineId === assumption.lineId) ===
      index,
  );
  const batchCount = listBatchConfirmable(assumptions).length;
  const codedCount = assumptions.length - batchCount;
  const codedHint = formatMessage({
    id: 'workspace.note.codedAssumptionsHint',
    defaultMessage:
      'Check each diagnosis code against what was said and confirm it on its own.',
  });

  const renderBasis = (assumption: NoteAssumption) => {
    const confirmable = assumption.coded && !!onConfirm;
    // A part is named, so the clinician sees which one waits.
    if (!assumption.quote && !confirmable && !assumption.part) {
      return null;
    }
    return (
      <li
        key={`${assumption.blockId}/${assumption.part ?? ''}`}
        className={styles.basis}
        data-component="workspace-note-assumption-basis"
      >
        <span className={styles.value}>{assumption.value}</span>
        {assumption.quote && (
          <>
            {' '}
            <AiBasisQuote
              className={styles.quote}
              quote={assumption.quote}
              said={assumption.said}
            />
          </>
        )}
        {confirmable && (
          <Button
            variant="secondary"
            size="tiny"
            icon="check"
            className={styles.confirmOne}
            aria-label={formatMessage(
              {
                id: 'workspace.note.confirmAssumptionValue',
                defaultMessage: 'Confirm “{value}”',
              },
              { value: assumption.value },
            )}
            data-component="workspace-note-confirm-coded-assumption"
            onClick={() => onConfirm(assumption)}
          >
            <FormattedMessage
              id="workspace.note.confirmCodedAssumption"
              defaultMessage="Confirm"
            />
          </Button>
        )}
      </li>
    );
  };

  return (
    <div
      role="status"
      className={cn('p2', styles.panel, className)}
      data-component="workspace-note-assumptions"
    >
      <p className={styles.title}>
        <FormattedMessage
          id="workspace.note.assumptionsTitle"
          defaultMessage="AI assumed or suggested {count, plural, one {# value} other {# values}} nobody said. Confirm or change {count, plural, one {it} other {them}} to save the note to the record:"
          values={{ count: assumptions.length }}
        />
      </p>
      <ul className={styles.lines}>
        {(allShown ? lines : lines.slice(0, SHOWN_LINES)).map(
          ({ lineId, sectionTitle, lineText }) => {
            const bases = assumptions
              .filter((assumption) => assumption.lineId === lineId)
              .map(renderBasis)
              .filter(Boolean);
            return (
              <li key={lineId}>
                {onSelect ? (
                  <button
                    type="button"
                    className={styles.link}
                    onClick={() => onSelect(lineId)}
                  >
                    {sectionTitle}: {lineText}
                  </button>
                ) : (
                  `${sectionTitle}: ${lineText}`
                )}
                {bases.length > 0 && <ul className={styles.bases}>{bases}</ul>}
              </li>
            );
          },
        )}
      </ul>
      {lines.length > SHOWN_LINES && (
        <button
          type="button"
          className={styles.link}
          aria-expanded={allShown}
          onClick={() => setAllShown((shown) => !shown)}
        >
          {allShown ? (
            <FormattedMessage
              id="workspace.note.unresolvedShowFewer"
              defaultMessage="Show fewer"
            />
          ) : (
            <FormattedMessage
              id="workspace.note.unresolvedShowAll"
              defaultMessage="Show all {count}"
              values={{ count: assumptions.length }}
            />
          )}
        </button>
      )}
      {codedCount > 0 && (
        <p
          className={styles.hint}
          data-component="workspace-note-coded-assumptions-hint"
        >
          {codedHint}
        </p>
      )}
      {onConfirmAll && batchCount > 0 && (
        <Button
          variant="secondary"
          size="small"
          icon="check"
          className={styles.confirmAll}
          title={codedCount > 0 ? codedHint : undefined}
          data-component="workspace-note-confirm-assumptions"
          onClick={onConfirmAll}
        >
          {codedCount > 0 ? (
            <FormattedMessage
              id="workspace.note.confirmOtherAssumptions"
              defaultMessage="Confirm the other {count, plural, one {value} other {# values}}"
              values={{ count: batchCount }}
            />
          ) : (
            <FormattedMessage
              id="workspace.note.confirmAssumptions"
              defaultMessage="Confirm all"
            />
          )}
        </Button>
      )}
    </div>
  );
};
